import { CARAVAN_ART } from "../config/caravanArt";
import { caravanTier, caravanState } from "../domain/treasuryCaravan";
import { treasuryPurchaseStorageKey } from "../config/treasuryTrial";
import { TRADE_QUARTER_ART } from "../config/tradeQuarter";
import type { AnalyticsEventName, AnalyticsProperties, GameAnalyticsPort } from "../domain/analytics";
import { walletFlowProperties } from "../domain/marketWalletFlow";
import type { MarketConnectedWallet, MarketOverview, MarketPort } from "../domain/market";
import { TreasuryError, treasuryProgress, type TreasuryPort, type TreasuryPurchaseStep, type TreasuryQuote, type TreasurySnapshot, type TreasuryTab } from "../domain/treasury";
import type { Locale } from "../domain/types";
import { createTreasuryGame, type TreasuryRuntime } from "../phaser/createTreasuryGame";
import { createTreasuryHourglass } from "../phaser/createTreasuryHourglass";
import { marketWalletPort, type MarketWalletPort } from "../services/MarketWallet";
import { awaitWalletConfirmation } from "../services/WalletConfirmation";
import { escapeHtml as esc } from "./templates";
import { treasuryCopy, treasuryError } from "./treasuryCopy";
import { caravanFigure, caravanRouteLabel, treasuryLiveIntro, treasuryLivePurchase, treasuryLoading, treasuryOffers, treasuryPayments, treasuryPositionDetails, treasuryPositions, treasuryTabs, treasuryTerms, treasuryTimeLeft, treasuryWallet } from "./treasuryViews";
import { installMarketNativeBack } from "./marketNativeBack";
import { installScrollBoundaryGuard } from "./scrollBoundaryGuard";
import { treasuryConfirmationEstimate } from "./treasuryConfirmation";

type Options = { root: HTMLElement; api: TreasuryPort; market: MarketPort; wallet?: MarketWalletPort; analytics?: GameAnalyticsPort; locale: Locale; reducedMotion: boolean;
  initialTab?: TreasuryTab; onExit(): void; onHome(): void };
type Route = { kind: "list" } | { kind: "quote" } | { kind: "live-intro" } | { kind: "purchase" } | { kind: "position"; id: string };
function required<T>(value: T | null): T { if (value === null) throw new Error("treasury_ui_missing"); return value; }

/** Treasury navigation and recoverable wallet handoff; authoritative state lives on the server. */
export class TreasuryController {
  readonly #options: Options;
  readonly #abort = new AbortController();
  readonly #page: HTMLElement;
  readonly #content: HTMLElement;
  readonly #tabs: HTMLElement;
  readonly #walletHost: HTMLElement;
  readonly #nativeBack: () => void;
  readonly #timer: ReturnType<typeof setInterval>;
  #runtime: TreasuryRuntime | null = null;
  #routeRuntime: TreasuryRuntime | null = null;
  #hourglass: { destroy(): void } | null = null;
  #preparationStartedAt = 0;
  #marketOverview: MarketOverview | null = null;
  #wallet: MarketConnectedWallet | null = null;
  #walletLoading = true;
  #walletConnecting = false;
  #walletError = false;
  #walletMenuOpen = false;
  #walletVersion = 0;
  #unwatchWallet: (() => void) | null = null;
  #cancelConnect: (() => void) | null = null;
  #unsubscribe: (() => void) | null = null;
  #snapshot: TreasurySnapshot | null = null;
  #tab: TreasuryTab;
  #route: Route = { kind: "list" };
  #selected = "";
  #quote: TreasuryQuote | null = null;
  #quoteRequest = "";
  #accepted = false;
  #busy = false;
  #loading = true;
  #error = "";
  #epoch = 0;
  #limit = 10;
  #listScroll = 0;
  #receivedAt = Date.now();
  #purchase: TreasuryPurchaseStep | null = null;
  #pendingPurchase: { requestId: string; planId: string; sentPhase: "reserve" | "arm" | "deposit" | "cancel" | null;
    confirmationStartedAt: number | null } | null = null;
  #walletPending = false;
  #purchasePollBusy = false;
  #purchaseWatch: (() => void) | null = null;
  #purchaseWatchId = "";
  #purchaseStreamLive = false;
  #purchaseChanged = false;
  #nextPurchasePollAt = 0;
  #lastContentMarkup = "";
  #lastContentBusy = false;
  #snapshotContent = "";
  constructor(options: Options) {
    this.#options = options; this.#tab = options.initialTab ?? "offers";
    const c = treasuryCopy(options.locale);
    options.root.innerHTML = `<section class="treasury-page" data-testid="treasury-page" aria-labelledby="treasury-title">
      <div class="treasury-hero"><img class="treasury-hero__background" src="${CARAVAN_ART.road}" alt="" width="1280" height="800" fetchpriority="high"/>
        <div class="treasury-scene" data-treasury-scene aria-hidden="true">${caravanFigure(0)}</div>
        <header class="treasury-header"><div class="treasury-world"><button type="button" class="treasury-home" data-treasury="home" aria-label="${esc(c.home)}"><img src="${TRADE_QUARTER_ART.crystal}" alt=""/></button><span>${esc(c.world)}</span></div><div data-treasury-wallet></div></header>
        <div class="treasury-heading"><h1 id="treasury-title" tabindex="-1">${esc(c.title)}</h1></div>
        <div class="treasury-hero__caption"><strong>${esc(c.hero)}</strong><p>${esc(c.heroNote)}</p></div>
      </div><div class="treasury-body"><div data-treasury-tabs></div><div data-treasury-content></div></div>
      <div class="visually-hidden" role="status" aria-live="polite" data-treasury-announcement></div>
    </section>`;
    this.#page = required(options.root.querySelector<HTMLElement>(".treasury-page"));
    this.#content = required(this.#page.querySelector<HTMLElement>("[data-treasury-content]"));
    this.#tabs = required(this.#page.querySelector<HTMLElement>("[data-treasury-tabs]"));
    this.#walletHost = required(this.#page.querySelector<HTMLElement>("[data-treasury-wallet]"));
    this.#page.dataset.reducedMotion = String(options.reducedMotion || window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    installScrollBoundaryGuard(this.#page, this.#abort.signal);
    this.#nativeBack = installMarketNativeBack(() => this.back());
    this.#page.addEventListener("click", event => {
      const target = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-treasury]") : null;
      if (target && !target.disabled) void this.#act(target);
    }, { signal: this.#abort.signal });
    this.#page.addEventListener("change", event => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches("[data-treasury-consent]")) return;
      this.#accepted = event.target.checked;
      const button = this.#content.querySelector<HTMLButtonElement>("[data-treasury=confirm], [data-treasury=begin-live]");
      if (button) button.disabled = !this.#accepted || this.#busy ||
        (button.dataset.treasury === "begin-live" && !this.#wallet);
    }, { signal: this.#abort.signal });
    window.addEventListener("online", () => { if (!this.#snapshot && !this.#loading) void this.#read(); }, { signal: this.#abort.signal });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden || this.#route.kind !== "purchase" || !this.#pendingPurchase) return;
      this.#nextPurchasePollAt = 0;
      if (!this.#busy) void this.#pollPurchase();
    }, { signal: this.#abort.signal });
    this.#timer = setInterval(() => this.#tick(), 1000);
    this.#render();
    try { this.#runtime = createTreasuryGame(required(this.#page.querySelector<HTMLElement>("[data-treasury-scene]")), options.reducedMotion); }
    catch { /* Visible art and all actions remain usable without canvas. */ }
    this.#unsubscribe = options.api.subscribe(state => this.#receive(state));
    void this.#read();
    void this.#readWallet();
  }
  destroy() {
    this.#epoch++; this.#abort.abort(); clearInterval(this.#timer);
    this.#unsubscribe?.(); this.#unwatchWallet?.(); this.#cancelConnect?.(); this.#nativeBack(); this.#runtime?.destroy(); this.#routeRuntime?.destroy(); this.#hourglass?.destroy();
    this.#purchaseWatch?.();
  }
  get #walletClient() { return this.#options.wallet ?? marketWalletPort; }
  #diagnostic(name: AnalyticsEventName, properties: AnalyticsProperties) {
    try {
      this.#options.analytics?.track(name, properties);
      void this.#options.analytics?.flush().catch(() => undefined);
    } catch { /* Diagnostics must not interrupt or retry a financial operation. */ }
  }
  #purchaseStorageKey(userId: string) { return treasuryPurchaseStorageKey(userId); }
  #restorePurchase(userId: string) {
    try {
      const saved = JSON.parse(localStorage.getItem(this.#purchaseStorageKey(userId)) ?? "null") as unknown;
      if (saved && typeof saved === "object" && "requestId" in saved && "planId" in saved &&
        typeof saved.requestId === "string" && /^[A-Za-z0-9:_-]{8,100}$/.test(saved.requestId) &&
        typeof saved.planId === "string" && /^[1-9][0-9]{0,8}$/.test(saved.planId)) {
        const sentPhase = "sentPhase" in saved &&
          (saved.sentPhase === "reserve" || saved.sentPhase === "arm" || saved.sentPhase === "deposit" || saved.sentPhase === "cancel") ? saved.sentPhase : null;
        const confirmationStartedAt = "confirmationStartedAt" in saved && typeof saved.confirmationStartedAt === "number" &&
          Number.isFinite(saved.confirmationStartedAt) && saved.confirmationStartedAt > 0 ? saved.confirmationStartedAt : null;
        this.#pendingPurchase = { requestId: saved.requestId, planId: saved.planId, sentPhase, confirmationStartedAt };
        this.#walletPending = sentPhase !== null; this.#render();
      }
    } catch { /* An inaccessible or damaged browser cache never creates a payment. */ }
  }
  #savePurchase(requestId: string, planId: string) {
    if (!this.#marketOverview) throw new TreasuryError("UNAVAILABLE");
    const record = { requestId, planId, sentPhase: null, confirmationStartedAt: null };
    try { localStorage.setItem(this.#purchaseStorageKey(this.#marketOverview.userId), JSON.stringify(record)); }
    catch { throw new TreasuryError("STORAGE"); }
    this.#pendingPurchase = record;
  }
  #markSentPhase(phase: "reserve" | "arm" | "deposit" | "cancel" | null) {
    if (!this.#pendingPurchase || !this.#marketOverview) throw new TreasuryError("UNAVAILABLE");
    const record = { ...this.#pendingPurchase, sentPhase: phase };
    try { localStorage.setItem(this.#purchaseStorageKey(this.#marketOverview.userId), JSON.stringify(record)); }
    catch { throw new TreasuryError("STORAGE"); }
    this.#pendingPurchase = record; this.#walletPending = phase !== null;
  }
  #clearPurchase() {
    if (!this.#marketOverview) throw new TreasuryError("UNAVAILABLE");
    try { localStorage.removeItem(this.#purchaseStorageKey(this.#marketOverview.userId)); }
    catch { throw new TreasuryError("STORAGE"); }
    this.#pendingPurchase = null; this.#walletPending = false;
    this.#syncPurchaseWatch();
  }
  #markConfirmationStarted(requestId: string) {
    const pending = this.#pendingPurchase;
    if (pending?.requestId !== requestId || pending.confirmationStartedAt !== null || !this.#marketOverview) return;
    const record = { ...pending, confirmationStartedAt: this.#now() };
    this.#pendingPurchase = record;
    try { localStorage.setItem(this.#purchaseStorageKey(this.#marketOverview.userId), JSON.stringify(record)); }
    catch { /* A display estimate must not turn an already sent payment into an error. */ }
  }
  #syncPurchaseWatch() {
    const id = this.#route.kind === "purchase" ? this.#pendingPurchase?.requestId ?? "" : "";
    if (id === this.#purchaseWatchId) return;
    this.#purchaseWatch?.(); this.#purchaseWatch = null;
    this.#purchaseWatchId = id; this.#purchaseStreamLive = false;
    if (!id || !this.#options.api.watchPurchase) return;
    this.#purchaseWatch = this.#options.api.watchPurchase(id, () => {
      if (this.#abort.signal.aborted || this.#purchaseWatchId !== id) return;
      this.#purchaseChanged = true; this.#nextPurchasePollAt = 0;
      if (!this.#busy) void this.#pollPurchase();
    }, state => {
      if (this.#abort.signal.aborted || this.#purchaseWatchId !== id) return;
      this.#purchaseStreamLive = state === "live";
      if (!this.#purchaseStreamLive) this.#nextPurchasePollAt = Math.min(this.#nextPurchasePollAt, Date.now() + 5000);
    });
  }
  async #readWallet() {
    const version = ++this.#walletVersion;
    this.#walletLoading = true; this.#walletError = false; this.#renderWallet();
    try {
      const overview = await this.#options.market.request<MarketOverview>("/");
      if (this.#abort.signal.aborted || version !== this.#walletVersion) return;
      const wallet = await this.#walletClient.read(overview, this.#options.locale);
      if (this.#abort.signal.aborted || version !== this.#walletVersion) return;
      this.#marketOverview = overview; this.#wallet = wallet;
      try { this.#options.analytics?.bindUser?.(overview.userId); } catch { /* Optional device diagnostics. */ }
      if (!this.#pendingPurchase) this.#restorePurchase(overview.userId);
      if (!this.#unwatchWallet) {
        const unwatch = await this.#walletClient.watch(overview, this.#options.locale, () => {
          if (!this.#abort.signal.aborted) void this.#readWallet();
        });
        if (this.#abort.signal.aborted) unwatch();
        else this.#unwatchWallet = unwatch;
      }
    } catch (error) {
      if (!this.#abort.signal.aborted && version === this.#walletVersion) {
        this.#marketOverview = null; this.#wallet = null; this.#walletError = true;
        this.#diagnostic("client_error", { screen: "treasury", operation: "wallet_read",
          code: error instanceof TreasuryError ? error.code : "UNAVAILABLE" });
      }
    } finally {
      if (!this.#abort.signal.aborted && version === this.#walletVersion) {
        this.#walletLoading = false; this.#render();
      }
    }
  }
  #renderWallet() {
    if (this.#abort.signal.aborted) return;
    const state = this.#walletConnecting ? "connecting" : this.#walletLoading ? "loading" : this.#walletError ? "error" : this.#wallet ? "connected" : "disconnected";
    this.#walletHost.innerHTML = treasuryWallet(this.#wallet, state, this.#snapshot?.mode === "preview", this.#options.locale, this.#walletMenuOpen);
    if (!this.#marketOverview) this.#walletHost.querySelector<HTMLButtonElement>("[data-treasury=connect-wallet]")?.setAttribute("disabled", "");
  }
  back() {
    if (this.#busy) return;
    if (this.#route.kind === "list") { this.#options.onExit(); return; }
    this.#epoch++; this.#route = { kind: "list" }; this.#quote = null; this.#error = ""; this.#loading = false;
    this.#runtime?.accent("idle");
    this.#render(); this.#page.scrollTop = this.#listScroll; this.#focus();
  }
  #now() { return this.#snapshot ? Date.parse(this.#snapshot.serverNow) + Math.max(0, Date.now() - this.#receivedAt) : Date.now(); }
  #receive(state: TreasurySnapshot) {
    if (this.#abort.signal.aborted || state.revision < (this.#snapshot?.revision ?? -1)) return;
    // A heartbeat/server clock update must not replace the scrolled document.
    const signature = JSON.stringify({ ...state, serverNow: undefined, revision: undefined });
    const changed = signature !== this.#snapshotContent;
    this.#snapshotContent = signature;
    this.#snapshot = state; this.#receivedAt = Date.now();
    const completedTicket = this.#purchase?.phase === "position" ? this.#purchase.ticket : null;
    let openedPosition = false;
    if (completedTicket && state.positions.some(p => p.id === completedTicket)) {
      try { this.#clearPurchase(); } catch { /* Keep the resume marker if storage cannot be updated. */ }
      if (this.#route.kind === "purchase") {
        this.#tab = "positions"; this.#route = { kind: "position", id: completedTicket };
        openedPosition = true;
      }
    }
    if (!state.plans.some(p => p.id === this.#selected && p.available)) this.#selected = state.plans.find(p => p.available)?.id ?? "";
    if ((changed || openedPosition) && this.#route.kind !== "quote") { this.#loading = false; this.#render(); }
  }
  async #read() {
    const epoch = ++this.#epoch; this.#loading = true; this.#error = ""; this.#render();
    try { const snapshot = await this.#options.api.read(); if (!this.#valid(epoch)) return; this.#receive(snapshot); }
    catch (error) { if (this.#valid(epoch)) this.#error = treasuryError(error, this.#options.locale); }
    finally { if (this.#valid(epoch)) { this.#loading = false; this.#render(); } }
  }
  #valid(epoch: number) { return !this.#abort.signal.aborted && this.#epoch === epoch; }
  #render() {
    if (this.#abort.signal.aborted) return;
    const scrollTop = Math.max(0, this.#page.scrollTop);
    const c = treasuryCopy(this.#options.locale), s = this.#snapshot, l = this.#options.locale;
    const focus = document.activeElement instanceof HTMLElement && (this.#content.contains(document.activeElement) || this.#tabs.contains(document.activeElement))
      ? { action: document.activeElement.dataset.treasury, id: document.activeElement.dataset.id, plan: document.activeElement.dataset.plan, tab: document.activeElement.dataset.tab } : null;
    this.#page.dataset.subpage = String(this.#route.kind !== "list");
    const caption = this.#page.querySelector<HTMLElement>(".treasury-hero__caption strong"), note = this.#page.querySelector<HTMLElement>(".treasury-hero__caption p");
    if (caption) caption.textContent = this.#tab === "offers" ? c.hero : this.#tab === "positions" ? c.positionsHero : c.paymentsHero;
    if (note) note.textContent = this.#tab === "offers" ? c.heroNote : this.#tab === "positions" ? c.positionsHeroNote : c.paymentsHeroNote;
    this.#tabs.innerHTML = treasuryTabs(this.#tab, l, s?.positions.length ?? 0);
    this.#tabs.hidden = this.#route.kind !== "list";
    this.#renderWallet();
    let html: string;
    if (this.#loading && (!s || this.#route.kind === "quote")) html = treasuryLoading(l, this.#route.kind === "quote");
    else if (!s) html = `<div class="treasury-state"><h2>${esc(c.errorTitle)}</h2><p>${esc(c.errorNote)}</p><button class="treasury-primary" type="button" data-treasury="retry">${esc(c.retry)}</button></div>`;
    else if (this.#route.kind === "quote") html = this.#quote ? treasuryTerms(this.#quote, l, this.#accepted, this.#busy) : `<div class="treasury-state"><h2>${esc(c.terms)}</h2><button class="treasury-primary" type="button" data-treasury="review">${esc(c.retry)}</button></div>`;
    else if (this.#route.kind === "live-intro") {
      const plan = s.plans.find(p => p.id === this.#selected && p.available);
      html = plan ? treasuryLiveIntro(plan, l, this.#accepted, !!this.#wallet) : treasuryOffers(s, this.#selected, l);
    }
    else if (this.#route.kind === "purchase") html = this.#purchase ? treasuryLivePurchase(this.#purchase, l, this.#walletPending,
      this.#pendingPurchase?.confirmationStartedAt ?? null) :
      treasuryLoading(l, true, this.#preparationStartedAt);
    else if (this.#route.kind === "position") {
      const id = this.#route.id, position = s.positions.find(p => p.id === id);
      html = position ? treasuryPositionDetails(s, position, l) : treasuryPositions(s, l, this.#limit);
    } else html = this.#tab === "offers" ? `${this.#pendingPurchase ? `<button type="button" class="treasury-live-resume" data-treasury="resume-live"><span><strong>${esc(c.liveResume)}</strong><small>${esc(c.liveResumeNote)}</small></span><span aria-hidden="true">›</span></button>` : ""}${treasuryOffers(s, this.#selected, l)}` : this.#tab === "positions" ? treasuryPositions(s, l, this.#limit) : treasuryPayments(s, l, this.#limit);
    const markup = `${this.#error ? `<p class="treasury-error" role="alert">${esc(this.#error)}</p>` : ""}${html}`;
    if (markup !== this.#lastContentMarkup || this.#busy !== this.#lastContentBusy) {
      this.#hourglass?.destroy(); this.#hourglass = null;
      this.#routeRuntime?.destroy(); this.#routeRuntime = null;
      this.#content.innerHTML = markup;
      const routeScene = this.#content.querySelector<HTMLElement>("[data-caravan-route]");
      if (routeScene) {
        try { this.#routeRuntime = createTreasuryGame(routeScene, this.#options.reducedMotion, true); }
        catch { /* The generated wagon and explicit route status remain visible. */ }
      }
      const hourglass = this.#content.querySelector<HTMLElement>("[data-treasury-hourglass]");
      if (hourglass) {
        try { this.#hourglass = createTreasuryHourglass(hourglass, this.#options.reducedMotion); }
        catch { /* The hourglass image and textual progress remain visible without canvas. */ }
      }
      this.#lastContentMarkup = markup; this.#lastContentBusy = this.#busy;
    }
    if (this.#busy) this.#content.querySelectorAll<HTMLButtonElement>("button").forEach(b => { b.disabled = true; });
    if (focus?.action) [...this.#page.querySelectorAll<HTMLElement>("[data-treasury]")].find(el => el.dataset.treasury === focus.action && el.dataset.id === focus.id && el.dataset.plan === focus.plan && el.dataset.tab === focus.tab)?.focus({ preventScroll: true });
    this.#tick(false);
    // Browser anchoring otherwise follows a replaced heading after a refresh.
    this.#page.scrollTop = scrollTop;
  }
  #focus() { const h = this.#content.querySelector<HTMLElement>("h2"); if (h) { h.tabIndex = -1; h.focus({ preventScroll: true }); } }
  #tick(refresh = true) {
    if (this.#abort.signal.aborted) return;
    const l = this.#options.locale, c = treasuryCopy(l);
    const snapshot = this.#snapshot;
    if (snapshot) {
      const id = this.#route.kind === "position" ? this.#route.id : null;
      const position = snapshot.positions.find(p => p.id === id);
      const heroPosition = position ?? (this.#tab === "positions" ? snapshot.positions.find(p => p.status === "active") : undefined);
      const plan = heroPosition?.plan ?? snapshot.plans.find(p => p.id === this.#selected);
      if (plan) {
        const tier = caravanTier(plan, snapshot.plans);
        this.#runtime?.setCaravan(tier, heroPosition ? caravanState(heroPosition, this.#now()) : this.#tab === "offers" ? "preview" : "ready");
        const fallback = this.#page.querySelector<HTMLElement>(".treasury-scene > .caravan-figure");
        if (fallback && fallback.dataset.tier !== String(tier)) fallback.outerHTML = caravanFigure(tier);
      }
      if (position) {
        this.#routeRuntime?.setCaravan(caravanTier(position.plan, snapshot.plans), caravanState(position, this.#now()));
        const label = this.#content.querySelector<HTMLElement>("[data-caravan-route-label]");
        if (label) label.textContent = caravanRouteLabel(position, this.#now(), l);
      }
    }
    this.#content.querySelectorAll<HTMLElement>("[data-price-at]").forEach(el => {
      if (this.#now() - Date.parse(el.dataset.priceAt ?? "") > 90_000) el.textContent = "GRIM";
    });
    const confirmation = this.#content.querySelector<HTMLElement>("[data-confirmation-countdown]");
    if (confirmation) {
      const estimate = treasuryConfirmationEstimate(this.#pendingPurchase?.confirmationStartedAt ?? null, this.#now());
      if (estimate) {
        const timer = confirmation.querySelector<HTMLElement>("[data-confirmation-time]");
        const label = confirmation.querySelector<HTMLElement>("[data-confirmation-label]");
        const delay = confirmation.querySelector<HTMLElement>("[data-confirmation-delay]");
        if (timer) { timer.hidden = estimate.delayed; timer.textContent = estimate.delayed ? "" : estimate.text; }
        if (label) label.hidden = estimate.delayed;
        if (delay) delay.hidden = !estimate.delayed;
      }
    }
    const preparation = this.#content.querySelector<HTMLElement>("[data-preparation-start]");
    if (preparation) {
      const seconds = Math.max(0, Math.floor(this.#now() / 1000 - Number(preparation.dataset.preparationStart)));
      const elapsed = preparation.querySelector<HTMLElement>("[data-preparation-elapsed]");
      if (elapsed) elapsed.textContent = `${c.livePrepareElapsed} ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
      const delay = preparation.querySelector<HTMLElement>("[data-preparation-delay]");
      if (delay) delay.hidden = seconds < 90;
    }
    const walletTimer = this.#content.querySelector<HTMLElement>("[data-wallet-deadline]");
    if (walletTimer) {
      const seconds = Math.max(0, Math.ceil(Number(walletTimer.dataset.walletDeadline) - this.#now() / 1000));
      walletTimer.textContent = seconds ? `${c.liveWalletTime}: ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : c.liveWalletExpired;
      if (!seconds) {
        const button = this.#content.querySelector<HTMLButtonElement>("[data-treasury=send-live]");
        if (button) button.disabled = true;
      }
    }
    this.#content.querySelectorAll<HTMLElement>("[data-matures-at]").forEach(el => { if (el.dataset.maturesAt) el.textContent = treasuryTimeLeft(el.dataset.maturesAt, this.#now(), l); });
    this.#content.querySelectorAll<HTMLElement>("[data-treasury-progress]").forEach(el => {
      const position = this.#snapshot?.positions.find(p => p.id === el.dataset.treasuryProgress);
      if (position) el.style.width = `${treasuryProgress(position, this.#now()) * 100}%`;
    });
    this.#syncPurchaseWatch();
    if (this.#route.kind === "purchase" && this.#pendingPurchase && !this.#busy && !this.#purchasePollBusy &&
      Date.now() >= this.#nextPurchasePollAt) void this.#pollPurchase();
    if (this.#route.kind !== "quote" || !this.#quote) return;
    const remaining = Math.max(0, Math.ceil((Date.parse(this.#quote.expiresAt) - this.#now()) / 1000));
    const timer = this.#content.querySelector<HTMLElement>("[data-quote-time]");
    if (timer) timer.textContent = remaining ? `${c.quoteTime}: ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}` : c.quoteUpdating;
    if (remaining === 0) {
      const button = this.#content.querySelector<HTMLButtonElement>("[data-treasury=confirm]"); if (button) button.disabled = true;
      if (refresh && !this.#busy && !this.#loading) void this.#quotePlan(true);
    }
  }
  async #quotePlan(expired = false) {
    const epoch = ++this.#epoch;
    if (this.#route.kind === "list") this.#listScroll = this.#page.scrollTop;
    this.#route = { kind: "quote" }; this.#quote = null; this.#accepted = false;
    this.#loading = true; this.#error = expired ? treasuryCopy(this.#options.locale).expired : "";
    this.#runtime?.accent("waiting"); this.#render();
    if (!expired) { this.#page.scrollTop = 0; this.#focus(); }
    try {
      const quote = await this.#options.api.quote(this.#selected); if (!this.#valid(epoch)) return;
      this.#quote = quote; this.#quoteRequest = crypto.randomUUID();
    } catch (error) { if (this.#valid(epoch)) this.#error = treasuryError(error, this.#options.locale); }
    finally { if (this.#valid(epoch)) { this.#loading = false; this.#runtime?.accent("idle"); this.#render(); if (!expired) this.#focus(); } }
  }
  async #confirm() {
    if (!this.#quote || !this.#accepted || this.#busy) return;
    if (Date.parse(this.#quote.expiresAt) <= this.#now()) { await this.#quotePlan(true); return; }
    const epoch = ++this.#epoch; this.#busy = true; this.#error = ""; this.#runtime?.accent("waiting"); this.#render();
    try {
      const position = await this.#options.api.open(this.#quote.id, this.#quoteRequest);
      if (!this.#valid(epoch)) return;
      const snapshot = await this.#options.api.read(); if (!this.#valid(epoch)) return;
      this.#receive(snapshot); this.#tab = "positions"; this.#route = { kind: "position", id: position.id }; this.#quote = null;
      this.#runtime?.accent("success");
      const announcement = this.#page.querySelector<HTMLElement>("[data-treasury-announcement]");
      if (announcement) announcement.textContent = treasuryCopy(this.#options.locale).pendingNote;
      this.#page.scrollTop = 0;
    } catch (error) {
      if (this.#valid(epoch)) this.#error = treasuryError(error, this.#options.locale);
      if (error instanceof TreasuryError && error.code === "EXPIRED") this.#accepted = false;
    } finally { if (this.#valid(epoch)) { this.#busy = false; this.#render(); this.#focus(); } }
  }
  #applyPurchaseStep(step: TreasuryPurchaseStep) {
    if (step.requestId !== this.#pendingPurchase?.requestId) return;
    if (step.phase === "not-started" && this.#pendingPurchase.sentPhase) {
      this.#error = treasuryCopy(this.#options.locale).liveWalletPending; this.#render(); return;
    }
    if (step.phase !== this.#purchase?.phase) this.#diagnostic("request_complete", {
      screen: "treasury", operation: "purchase_phase", requestId: step.requestId, phase: step.phase,
    });
    const firstPosition = step.phase === "position" && this.#purchase?.phase !== "position";
    if (step.phase !== this.#pendingPurchase.sentPhase && this.#pendingPurchase.sentPhase !== null) {
      try { this.#markSentPhase(null); } catch { /* Keep the conservative pending marker on storage failure. */ }
    }
    if (step.preparation) {
      this.#preparationStartedAt = this.#preparationStartedAt ? Math.min(this.#preparationStartedAt, step.preparation.startedAt) : step.preparation.startedAt;
      step = { ...step, preparation: { ...step.preparation, startedAt: this.#preparationStartedAt } };
    }
    this.#purchase = step; this.#error = "";
    this.#runtime?.accent(step.phase === "position" ? "success" : "waiting");
    if (step.phase === "position") {
      const announcement = this.#page.querySelector<HTMLElement>("[data-treasury-announcement]");
      if (announcement) announcement.textContent = treasuryCopy(this.#options.locale).livePositionTitle;
      if (firstPosition) void this.#read();
    }
    this.#render();
  }
  async #beginLivePurchase() {
    if (this.#busy || !this.#accepted || !this.#wallet || !this.#snapshot?.purchasesEnabled ||
      this.#snapshot.mode !== "live" || !this.#options.api.startPurchase) return;
    if (this.#pendingPurchase) {
      // A new intent must not force the user through a cancelled attempt. Ask
      // the server first: a sent/unknown transfer must NEVER be silently replaced.
      const epoch = ++this.#epoch;
      this.#route = { kind: "purchase" }; this.#purchase = null; this.#busy = true;
      this.#preparationStartedAt = Math.floor(this.#now() / 1000); this.#error = ""; this.#render();
      try {
        if (!this.#options.api.purchaseStatus) throw new TreasuryError("UNAVAILABLE");
        const step = await this.#options.api.purchaseStatus(this.#pendingPurchase.requestId);
        if (!this.#valid(epoch)) return;
        if (step.phase === "cancelled" || step.phase === "position" || (step.phase === "expired" && step.restartable)) {
          this.#clearPurchase(); this.#purchase = null;
        } else { this.#applyPurchaseStep(step); return; }
      } catch (error) {
        if (this.#valid(epoch)) this.#error = treasuryError(error, this.#options.locale);
        return;
      } finally { if (this.#valid(epoch)) { this.#busy = false; this.#render(); } }
    }
    const requestId = crypto.randomUUID().replaceAll("-", ""), planId = this.#selected;
    try { this.#savePurchase(requestId, planId); }
    catch (error) { this.#error = treasuryError(error, this.#options.locale); this.#render(); return; }
    await this.#prepareLivePurchase();
  }
  async #prepareLivePurchase() {
    const pending = this.#pendingPurchase;
    if (this.#busy || !pending || pending.sentPhase || !this.#options.api.startPurchase) return;
    const { requestId, planId } = pending;
    const epoch = ++this.#epoch;
    this.#route = { kind: "purchase" }; this.#purchase = null; this.#walletPending = false;
    this.#preparationStartedAt = Math.floor(this.#now() / 1000);
    this.#busy = true; this.#error = ""; this.#nextPurchasePollAt = Date.now() + 12_000;
    this.#render(); this.#page.scrollTop = 0;
    try {
      const step = await this.#options.api.startPurchase(requestId, planId);
      if (this.#valid(epoch)) this.#applyPurchaseStep(step);
    } catch (error) {
      if (this.#valid(epoch)) {
        this.#purchase = { phase: "not-started", requestId };
        this.#error = treasuryError(error, this.#options.locale);
        this.#diagnostic("client_error", { screen: "treasury", operation: "purchase_start", requestId });
      }
    } finally {
      if (this.#valid(epoch)) { this.#busy = false; this.#render(); this.#focus(); }
    }
  }
  async #pollPurchase() {
    const requestId = this.#pendingPurchase?.requestId;
    if (!requestId || !this.#options.api.purchaseStatus || this.#purchasePollBusy) return;
    const epoch = this.#epoch;
    this.#purchasePollBusy = true; this.#nextPurchasePollAt = Date.now() + 5_000;
    this.#purchaseChanged = false;
    try {
      const step = await this.#options.api.purchaseStatus(requestId);
      if (this.#valid(epoch) && this.#route.kind === "purchase") this.#applyPurchaseStep(step);
    } catch {
      if (this.#valid(epoch) && this.#route.kind === "purchase") {
        this.#error = treasuryCopy(this.#options.locale).liveSyncNote;
        this.#diagnostic("client_error", { screen: "treasury", operation: "purchase_status", requestId });
        this.#render();
      }
    } finally {
      this.#purchasePollBusy = false;
      // A live SSE connection does not guarantee that every wakeup arrived.
      // Keep confirming purchases fresh; reads can never send another deposit.
      this.#nextPurchasePollAt = this.#purchaseChanged ? 0 : Date.now() + 5_000;
    }
  }
  async #sendLivePurchase() {
    const step = this.#purchase, overview = this.#marketOverview;
    if (!step || !overview || !this.#wallet || this.#busy || this.#walletPending ||
      (step.phase !== "reserve" && step.phase !== "arm" && step.phase !== "deposit" && step.phase !== "cancel")) return;
    if (step.walletRequest.validUntil * 1000 <= Date.now()) {
      this.#nextPurchasePollAt = 0; await this.#pollPurchase(); return;
    }
    try { this.#markSentPhase(step.phase); }
    catch (error) { this.#error = treasuryError(error, this.#options.locale); this.#render(); return; }
    const epoch = ++this.#epoch;
    let handedOff = false;
    const controller = new AbortController();
    this.#busy = true; this.#error = ""; this.#runtime?.accent("waiting"); this.#render();
    try {
      const pending = this.#walletClient.send(step.walletRequest, overview, this.#options.locale, {
        now: () => Date.now(), signal: controller.signal,
        event: event => this.#diagnostic("market_wallet", walletFlowProperties({ ...event, kind: "vault", operationId: step.requestId })),
        onRequestSent: () => {
          handedOff = true;
          if (this.#valid(epoch)) this.#render();
        },
      });
      await awaitWalletConfirmation(pending, {
        expiresAt: step.walletRequest.validUntil * 1000, now: () => Date.now(), expired: () => undefined,
        late: async () => { if (this.#valid(epoch)) {
          if (step.phase === "deposit") this.#markConfirmationStarted(step.requestId);
          this.#nextPurchasePollAt = 0; void this.#pollPurchase();
        } },
      });
      if (this.#valid(epoch)) {
        if (step.phase === "deposit") this.#markConfirmationStarted(step.requestId);
        this.#walletPending = true;
      }
    } catch {
      if (this.#valid(epoch)) {
        if (!handedOff) {
          try { this.#markSentPhase(null); }
          catch { /* A pending marker is safer than offering a duplicate transfer. */ }
          this.#error = treasuryCopy(this.#options.locale).liveWalletFailed;
        }
      }
    } finally {
      controller.abort();
      if (this.#valid(epoch)) {
        this.#busy = false; this.#nextPurchasePollAt = 0; this.#render();
      }
    }
  }
  async #act(button: HTMLButtonElement) {
    const action = button.dataset.treasury;
    if (this.#busy) return;
    if (action === "back") { this.back(); return; }
    if (action === "home") { this.#options.onHome(); return; }
    if (action === "toggle-wallet") { this.#walletMenuOpen = !this.#walletMenuOpen; this.#renderWallet(); return; }
    if (action === "retry") { await this.#read(); return; }
    if (action === "retry-wallet") { await this.#readWallet(); return; }
    if (action === "disconnect-wallet" || action === "change-wallet") {
      if (!this.#marketOverview || this.#walletConnecting) return;
      this.#walletConnecting = true; this.#renderWallet();
      try {
        await this.#walletClient.disconnect(this.#options.market, this.#marketOverview, this.#options.locale);
        this.#wallet = null; this.#walletMenuOpen = false; await this.#readWallet();
      } catch { this.#walletError = true; }
      finally { this.#walletConnecting = false; this.#renderWallet(); }
      if (action === "disconnect-wallet" || this.#walletError) return;
    }
    if (action === "connect-wallet" || action === "change-wallet") {
      if (!this.#marketOverview || this.#walletConnecting) return;
      this.#walletConnecting = true; this.#walletError = false; this.#renderWallet();
      try {
        this.#cancelConnect?.();
        this.#cancelConnect = await this.#walletClient.connect(this.#options.market, this.#marketOverview, this.#options.locale,
          () => { this.#walletConnecting = false; void this.#readWallet(); },
          () => { this.#walletConnecting = false; this.#walletError = true; this.#renderWallet(); });
        if (this.#abort.signal.aborted) this.#cancelConnect();
      } catch {
        this.#walletConnecting = false; this.#walletError = true; this.#renderWallet();
      }
      return;
    }
    if (action === "tab") {
      const tab = button.dataset.tab; if (tab !== "offers" && tab !== "positions" && tab !== "payments") return;
      this.#epoch++; this.#tab = tab; this.#route = { kind: "list" }; this.#quote = null; this.#loading = false; this.#error = ""; this.#limit = 10;
      this.#runtime?.accent("idle");
      this.#render(); this.#page.scrollTop = Math.min(this.#page.scrollTop, 260); this.#focus(); return;
    }
    if (action === "select") { this.#selected = button.dataset.plan ?? ""; this.#render(); this.#runtime?.accent("success"); return; }
    if (action === "review") {
      if (this.#snapshot?.mode === "live") {
        if (!this.#snapshot.purchasesEnabled) return;
        this.#listScroll = this.#page.scrollTop; this.#accepted = false;
        this.#route = { kind: "live-intro" }; this.#error = ""; this.#render(); this.#page.scrollTop = 0; this.#focus();
      } else await this.#quotePlan();
      return;
    }
    if (action === "begin-live") { await this.#beginLivePurchase(); return; }
    if (action === "retry-live") { await this.#prepareLivePurchase(); return; }
    if (action === "resume-live") {
      if (!this.#pendingPurchase) return;
      this.#epoch++; this.#route = { kind: "purchase" }; this.#nextPurchasePollAt = 0;
      this.#render(); this.#page.scrollTop = 0; this.#focus(); return;
    }
    if (action === "restart-live") {
      if (!this.#pendingPurchase || !(this.#purchase?.phase === "cancelled" ||
        (this.#purchase?.phase === "expired" && this.#purchase.restartable))) return;
      try { this.#clearPurchase(); }
      catch (error) { this.#error = treasuryError(error, this.#options.locale); this.#render(); return; }
      this.#epoch++; this.#purchase = null; this.#error = "";
      if (this.#accepted && this.#wallet) await this.#beginLivePurchase();
      else { this.#route = { kind: "live-intro" }; this.#render(); this.#page.scrollTop = 0; this.#focus(); }
      return;
    }
    if (action === "send-live") { await this.#sendLivePurchase(); return; }
    if (action === "confirm") { await this.#confirm(); return; }
    if (action === "position" && button.dataset.id) {
      this.#listScroll = this.#page.scrollTop; this.#route = { kind: "position", id: button.dataset.id }; this.#error = "";
      this.#render(); this.#page.scrollTop = 0; this.#focus(); return;
    }
    if (action === "more") { this.#limit += 10; this.#render(); return; }
    if (action === "examples" && this.#options.api.showExamples) {
      const epoch = ++this.#epoch; this.#busy = true; this.#error = ""; this.#render();
      try { await this.#options.api.showExamples(); if (this.#valid(epoch)) this.#receive(await this.#options.api.read()); }
      catch (error) { if (this.#valid(epoch)) this.#error = treasuryError(error, this.#options.locale); }
      finally { if (this.#valid(epoch)) { this.#busy = false; this.#render(); } }
    }
  }
}
